import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Typography,
  InputAdornment,
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
} from "@mui/material";
import { useFormik } from "formik";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../store/hooks/store.hooks";
import EuroIcon from "@mui/icons-material/Euro";
import CurrencyRubleIcon from "@mui/icons-material/CurrencyRuble";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CloseIcon from "@mui/icons-material/Close";
import "./modal.css";
import { IDepositModalProps } from "../typing";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { ProfileMenuSelector } from "../../ProfileMenu/ProfileMenu.selector";
import { Loader } from "../../../loader/loader";
import { createDepositPayment } from "../../../../store/models/payment/payment.thunk";
import { DepositWarningModal } from "./DepositWarningModal";
import {
  currencies,
  plusSumsForDeposits,
} from "../../../../constants/Constants";
import { getCurrencyLabel } from "../../../../utils/payment";

export default function DepositModal({
  open,
  loading,
  method_id,
  image,
  min,
  max,
  amount_changeable,
  paymentCurrency,
  closeHandler,
  depositHandler,
}: IDepositModalProps) {
  const { t, i18n } = useTranslation();
  const { currency } = useAppSelector(ProfileMenuSelector);
  const [openPromocode, setOpenPromocode] = useState(false);
  const [promocode, setPromocode] = useState("");
  const PaymentSchema = depositSchema(min, max, paymentCurrency, i18n);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const initAmount = Number(sessionStorage.getItem("enamsl"));
    const initPromocode = sessionStorage.getItem("enprsl");

    // debugger;
    if (initAmount) {
      if (+initAmount > min) {
        formik.setValues({ ...formik.values, amount: initAmount });
      } else {
        formik.setValues({ ...formik.values, amount: min });
      }
    }

    if (initPromocode) {
      setOpenPromocode(true);
      formik.setValues({ ...formik.values, promocode: initPromocode });
    }
  }, [open]);

  const handleModalClose = () => {
    setShowWarningModal(false);
  };

  const handleDeposit = () => {
    if (method_id && formik.values.amount !== null) {
      setShowWarningModal(false);
      depositHandler(
        Number(formik.values.amount),
        method_id,
        formik.values.promocode
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      amount: Number(sessionStorage.getItem("enamsl")) || null,
      promocode: "",
    },
    validationSchema: PaymentSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (values.amount != null) {
        if (amount_changeable) {
          setShowWarningModal(true);
        } else {
          handleDeposit();
        }
      }
    },
  });
  const onChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    const regExp = /^\d$/i;
    if (regExp.test(value[value.length - 1])) {
      formik.setValues({ ...formik.values, amount: Number(value) });
    }
    if (value === "") {
      formik.setValues({ ...formik.values, amount: null });
    }
  };
  return (
    <Dialog
      open={open}
      id="payment__modal"
      onClose={closeHandler}
      sx={{
        height: {
          xs: "calc(var(--real-vh) - 56px - 80px)",
          sm: "calc(var(--real-vh) - 84px)",
        },
        top: { xs: "56px", sm: "84px" },

        overflowY: "auto",
        ".MuiDialog-container": {
          height: "auto",
          alignItems: { xs: "flex-start", sm: "center" },
          padding: {
            xs: "56px 0 80px",
            sm: "84px 0 80px",
          },
          marginTop: "0px",
        },
      }}
      PaperProps={{
        sx: {
          margin: "0",
          width: "100%",
          maxWidth: "400px",
          height: "auto",
          maxHeight: "100%",
          borderRadius: "var(--primary-border-radius)",
          overflowY: "unset",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        <DialogTitle>{t("main.profile.payments.deposit.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t("main.profile.payments.deposit.method")}
          </Typography>
          <img
            src={`https://${process.env.REACT_APP_IMAGE_DOMAIN}/payments/${image}`}
            alt="Payment method"
          />
          <Typography
            variant="body1"
            sx={{ textAlign: "center", color: "var(--secondary-text-color)" }}
          >{`(${min} - ${max} ${paymentCurrency})`}</Typography>
        </DialogContent>
        <form onSubmit={formik.handleSubmit} className="payment-modal__form">
          <label htmlFor="amount">
            <Typography
              variant="body1"
              sx={{
                display: "block",
                color: "var(--primary-text-color)",
                fontSize: "14px",
                lineHeight: " 14px",
                marginBottom: "8.2px !important",
                fontWeight: "400 !important",
              }}
            >
              {t("main.profile.payments.deposit.amount")}
            </Typography>
          </label>
          <Box style={{ position: "relative" }}>
            <TextField
              error={Boolean(formik.errors.amount && formik.touched.amount)}
              size="small"
              margin="normal"
              autoComplete="off"
              className="payment-modal__amount"
              id="amount"
              type="text"
              fullWidth
              variant="standard"
              value={formik.values.amount ?? ""}
              onChange={onChangeHandler}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {getCurrencyLabel(currency)}
                  </InputAdornment>
                ),
              }}
            />
            {formik.errors.amount && formik.touched.amount ? (
              <Typography
                className="Typography-error"
                component="p"
                variant="body2"
                sx={{
                  position: "absolute",
                  left: "10px",
                  bottom: "10px",
                }}
              >
                {formik.errors.amount}
              </Typography>
            ) : null}
          </Box>
          <Box className="payment-modal__button-container">
            {plusSumsForDeposits
              .filter((s) => s.currency === currency)[0]
              .amounts?.map((amount, i) => (
                <button
                  key={i}
                  type="button"
                  className="payment-modal__button"
                  onClick={() => {
                    formik.setValues({
                      ...formik.values,
                      amount: (formik.values.amount ?? 0) + amount.value,
                    });
                  }}
                >
                  {amount.text}
                </button>
              ))}
          </Box>
          {openPromocode ? (
            <>
              <label htmlFor="promocode">
                <Typography
                  variant="body1"
                  sx={{
                    display: "block",
                    color: "var(--primary-text-color)",
                    fontSize: "14px",
                    lineHeight: " 14px",
                    marginBottom: "8.2px !important",
                    fontWeight: "400 !important",
                  }}
                >
                  {t("main.profile.payments.deposit.promocode")}
                </Typography>
              </label>
              <Box style={{ position: "relative" }}>
                <TextField
                  error={Boolean(formik.errors.promocode)}
                  style={{ position: "relative" }}
                  size="small"
                  margin="normal"
                  fullWidth
                  id="promocode"
                  placeholder={t("registration.fields.promocode")}
                  name="promocode"
                  autoComplete="off"
                  variant="outlined"
                  onChange={(e) => {
                    formik.handleChange(e);
                    setPromocode(e.currentTarget.value);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.promocode
                    .toUpperCase()
                    .replace(/\s/g, "")}
                  sx={{
                    fontSize: "12px",
                    lineHeight: "14px",
                    margin: "0 0 24px 0",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        border: "none",
                        top: "0",
                      },
                    },
                  }}
                  InputProps={{
                    componentsProps: {
                      input: {
                        style: {
                          padding: "7px 16px",
                          fontSize: "12px",
                          margin: "0px",
                          lineHeight: "14px",
                        },
                      },
                    },
                  }}
                />
                {formik.errors.promocode && formik.touched.promocode ? (
                  <Typography
                    className="Typography-error"
                    component="p"
                    variant="body2"
                    sx={{
                      position: "absolute",
                      left: "10px",
                      bottom: "10px",
                    }}
                  >
                    {formik.errors.promocode}
                  </Typography>
                ) : null}
              </Box>
            </>
          ) : (
            <Box
              style={{ cursor: "pointer" }}
              onClick={() => setOpenPromocode(true)}
              sx={{
                fontWeight: "400 !important",
                color: "var(--primary-text-color)",
                fontSize: "12px",
                lineHeight: "18px",
                margin: "0 0 24px 5px",
                opacity: "0.75",
              }}
            >
              {t("registration.fields.confirmPromocode")}
            </Box>
          )}
          <DialogActions
            sx={{ justifyContent: "center", alignItems: "center" }}
          >
            <button
              className="payment-modal__close"
              type="button"
              onClick={() => {
                formik.setValues({ ...formik.values, amount: 0 });
                closeHandler();
              }}
            >
              <CloseIcon
                style={{
                  color: "rgba(255,255,255,0.7)",
                }}
              />
            </button>
            <button className="payment-modal__submit" type="submit">
              {t("buttons.shorttopup")}
            </button>
          </DialogActions>
        </form>

        {/* <AmountSelectionModal
          open={showSuggesteModal}
          amounts={suggestedAmounts}
          onClose={handleModalClose}
          onSelectAmount={handleAmountSelect}
          currentAmount={Number(formik.values.amount)}
        /> */}
        <DepositWarningModal
          open={showWarningModal}
          onClose={handleModalClose}
          submit={handleDeposit}
        />
      </Box>
      {loading && <Loader />}
    </Dialog>
  );
}

function depositSchema(min: number, max: number, currency: string, i18n: any) {
  return Yup.object().shape({
    amount: Yup.number()
      .required(i18n.t("main.profile.payments.deposit.validation.required"))
      .min(
        min,
        i18n.t("main.profile.payments.deposit.validation.min", {
          val: `${min} ${currency}`,
        })
      )
      .max(
        max,
        i18n.t("main.profile.payments.deposit.validation.max", {
          val: `${max} ${currency}`,
        })
      ),
    promocode: Yup.string().matches(
      /^[a-zA-Z0-9]*$/g,
      i18n.t("validationschema.promoCodeValid")
    ),
  });
}
